import {CategoryModel} from './CategoryModel';
import {Contact, ImageSource, LngLatGeolocation, Provider} from '../types';
import {Address} from '../types/Address';
import {ContactType} from '../enums/ContactType';
import {Builder} from '../core/Builder';
import {MultiLanguageTextModel} from './MultiLanguageTextModel';
import {EventDateModel} from './EventDateModel';
import {DescriptionModel} from './DescriptionModel';
import {DescriptionType} from '../enums/DescriptionType';
import {Description} from '../types/Description';
import {Language} from '../enums/Language';
import {isInstagramUrl} from '../utils/mediaUtils';
import moment from 'moment';
import {hashCode} from '../utils/hash';
import {Tag} from '../types/Tag';
import {Organizer} from '../types/Organizer';
import {EventLocation} from '../types/EventLocation';
import {EventAttributes, DataSourceProviderInfo} from '../types/EventAttributes';

export class BalloonEventModelBuilder extends Builder<BalloonEventModel> {
    private _id: string = '';
    private _title: string = '';
    private _slug: string = '';
    private _descriptions: Description[] = [];
    private _tags: Tag[] = [];
    private _fullLocation: Partial<EventLocation> = {contactInfos: []};
    private _categories: CategoryModel[] = [];
    private _dates: EventDateModel[] = [];
    private _contacts: Contact[] = [];
    private _images: ImageSource[] = [];
    private _provider: Provider | null = null;
    private _lastModified: Date | null = null;
    private _sorting: number = 0;
    private _collectionId: string | null = null;
    private _organizer: Organizer | null = null;
    private _attributes: EventAttributes = {};
    private _logo: string | null = null;
    private _creator: { name: string; slug: string } | null = null;

    id(id: string) {
        this._id = id;
        return this;
    }

    title(title: string) {
        this._title = title;
        return this;
    }

    slug(slug: string) {
        this._slug = slug;
        return this;
    }

    description(descriptions: Description[]) {
        this._descriptions = descriptions;
        return this;
    }

    tags(tags: Tag[]) {
        this._tags = tags;
        return this;
    }

    fullLocation(location: EventLocation) {
        this._fullLocation = location;
        return this;
    }

    categories(categories: CategoryModel[]) {
        this._categories = categories;
        return this;
    }

    dates(dates: EventDateModel[]) {
        this._dates = dates;
        return this;
    }

    contacts(contacts: Contact[]) {
        this._contacts = contacts;
        return this;
    }

    images(images: ImageSource[]) {
        this._images = images;
        return this;
    }

    source(source: Provider) {
        this._provider = source;
        return this;
    }

    lastModified(lastModified: Date) {
        this._lastModified = lastModified;
        return this;
    }

    sorting(sorting: number) {
        this._sorting = sorting;
        return this;
    }

    collectionId(collectionId: string) {
        this._collectionId = collectionId;
        return this;
    }

    organizer(organizer: Organizer) {
        this._organizer = organizer;
        return this;
    }

    attributes(attributes: EventAttributes) {
        this._attributes = attributes;
        return this;
    }

    logo(logo: string | null) {
        this._logo = logo;
        return this;
    }

    creator(creator: { name: string; slug: string } | null) {
        this._creator = creator;
        return this;
    }

    build() {
        return new BalloonEventModel(
            this._id, // assigned by the backend

            this._slug || this._id, // assigned by the backend
            this._title,
            new DescriptionModel(this._descriptions),
            this._tags,
            this._fullLocation as EventLocation,
            this._categories,
            this._dates,
            this._contacts,
            this._images,

            this._provider, // assigned by the backend
            this._lastModified!, // assigned by the backend
            this._sorting, // assigned by the backend
            this._collectionId,
            this._organizer,
            this._attributes,
            this._logo,
            this._creator
        );
    }
}

export class BalloonEventModelDraftBuilder extends Builder<BalloonEventModel> {
    private _title: string = '';
    private _descriptions: Description[] = [];
    private _tags: string[] = [];
    private _fullLocation: Partial<EventLocation> = {contactInfos: []};
    private _categories: CategoryModel[] = [];
    private _dates: EventDateModel[] = [];
    private _contacts: Contact[] = [];
    private _images: ImageSource[] = [];
    private _organizer: Organizer | null = null;
    private _provider: Provider | null = null;
    private _attributes: EventAttributes = {};

    clear() {
        this._title = '';
        this._descriptions = [];
        this._tags = [];
        this._fullLocation = {};
        this._categories = [];
        this._dates = [];
        this._contacts = [];
        this._images = [];
        this._organizer = null;
        this._attributes = {};
        return this;
    }

    title(title: string) {
        this._title = title;
        return this;
    }

    description(descriptions: Description[]) {
        this._descriptions = descriptions;
        return this;
    }

    tags(tags: string[]) {
        this._tags = tags;
        return this;
    }

    location(location: LngLatGeolocation) {
        this._fullLocation.coordinates = location;
        return this;
    }

    address(address: Address) {
        this._fullLocation.address = address;
        return this;
    }

    locationName(locationName: string) {
        this._fullLocation.name = locationName;
        return this;
    }

    categories(categories: CategoryModel[]) {
        this._categories = categories;
        return this;
    }

    dates(dates: EventDateModel[]) {
        this._dates = dates;
        return this;
    }

    contacts(contacts: Contact[]) {
        this._contacts = contacts;
        return this;
    }

    images(images: ImageSource[]) {
        this._images = images;
        return this;
    }

    organizer(organizer: Organizer) {
        this._organizer = organizer;
        return this;
    }

    provider(provider: Provider) {
        this._provider = provider;
        return this;
    }

    attributes(attributes: EventAttributes) {
        this._attributes = attributes;
        return this;
    }

    build(): BalloonEventModel {
        return new BalloonEventModel(
            hashCode(this._title).toString(), // assigned by the backend
            hashCode(this._title).toString(),
            this._title,
            new DescriptionModel(this._descriptions),
            this._tags.map((t) => ({name: t, slug: t, id: t, type: ''})),
            this._fullLocation as EventLocation,
            this._categories,
            this._dates!,
            this._contacts,
            this._images,
            this._provider,
            new Date(), // assigned by the backend
            1, // assigned by the backend
            null,
            this._organizer,
            this._attributes
        );
    }
}

const EMPTY_MULTILANGUAGE_TEXT = new MultiLanguageTextModel([]);

export class BalloonEventModel {
    _id: string;
    _slug: string;
    _title: string;
    _complexDescription: DescriptionModel;
    _fullTags: Tag[];
    _tags: string[];
    _fullLocation: EventLocation;
    _location: LngLatGeolocation;
    _address: Address;
    _locationName: string;
    _categories: CategoryModel[];
    _dates: EventDateModel[];
    _contacts: Contact[];
    _images: ImageSource[];
    _source: string;
    _provider: Provider | null;
    _lastModified: Date;
    _sorting: number | null;
    _collectionId: string | null = null;
    _regionAreas: string[] = [];
    _organizer: Organizer | null = null;
    _attributes: EventAttributes = {};
    _logo: string | null = null;
    _creator: { name: string; slug: string } | null = null;
    _x?: number;
    _y?: number;

    constructor(
        id: string,
        slug: string,
        title: string,
        description: DescriptionModel,
        tags: Tag[],
        location: EventLocation,
        categories: CategoryModel[],
        dates: EventDateModel[],
        contacts: Contact[],
        images: ImageSource[],
        source: Provider | null,
        lastModified: Date,
        sorting: number | null,
        collectionId?: string | null,
        organizer?: Organizer | null,
        attributes?: EventAttributes,
        logo?: string | null,
        creator?: { name: string; slug: string } | null
    ) {
        this._id = id;
        this._slug = slug;
        this._title = title;
        this._complexDescription = description;
        this._fullTags = tags;
        this._fullLocation = location;
        this._location = {
            lat: Number(location.coordinates.lat),
            lng: Number(location.coordinates.lng),
        };
        this._address = location.address;
        this._locationName = location.name;
        this._categories = categories;
        this._dates = dates;
        this._contacts = contacts;
        this._images = images;
        this._source = source?.name || '';
        this._provider = source;
        this._lastModified = lastModified;

        this._sorting = sorting;
        this._collectionId = collectionId || null;

        this._organizer = organizer || null;
        this._attributes = attributes || {};
        this._logo = logo || null;
        this._creator = creator || null;
        this._tags = tags.map((t) => t.slug);
    }
    public setScreenCoordinates(x: number, y: number) {
        this._x = x;
        this._y = y;
    }
    public getScreenCoordinates() {
        return {x: this._x, y: this._y};
    }
    public static from(data: BalloonEventModel) {
        return new BalloonEventModel(
            data._id,
            data._slug,
            data._title,
            DescriptionModel.from(data._complexDescription),
            data._fullTags,
            data._fullLocation,
            data._categories.map((c) => CategoryModel.from(c)),
            data._dates.map((d) => EventDateModel.from(d)),
            data._contacts,
            data._images,
            data._provider,
            new Date(data._lastModified),
            data._sorting,
            data._collectionId,
            data._organizer,
            data._attributes,
            data._logo,
            data._creator
        );
    }

    update(other: BalloonEventModel) {
        if (other._id) {
            this._id = other._id;
        }
        if (other._slug) {
            this._slug = other._slug;
        }
        if (other._title) {
            this._title = other._title;
        }
        if (other._complexDescription) {
            this._complexDescription = other._complexDescription;
        }
        if (other._location) {
            this._location = {...this._location, ...other._location};
        }
        if (other._address) {
            if (!this._address) this._address = other._address;
            else {
                if (other._address.country)
                    this._address.country = other._address.country;
                if (other._address.locality)
                    this._address.locality = other._address.locality;
                if (other._address.region) this._address.region = other._address.region;
                if (other._address.street) this._address.street = other._address.street;
                if (other._address.zip) this._address.zip = other._address.zip;
            }
        }
        if (other._locationName) {
            this._locationName = other._locationName;
        }
        if (other._categories) {
            this._categories = other._categories;
        }
        if (other._dates) {
            this._dates = other._dates;
        }
        if (other._contacts) {
            for (const contact of other._contacts) {
                if (!this._contacts.find((c) => c.type === contact.type))
                    this._contacts.push(contact);
            }
        }
        if (other._images) {
            for (const image of other._images) {
                const existingImageIndex = this._images.findIndex((i) => i.url === image.url);
                if (existingImageIndex === -1) {
                    // New image - add it
                    this._images.push(image);
                } else {
                    // Image exists - update it with new data (including copyright)
                    this._images[existingImageIndex] = image;
                }
            }
        }
        if (other.provider) {
            this.provider = other.provider;
        }
        if (other._lastModified) {
            this._lastModified = other._lastModified;
        }
        if (other._sorting != null) {
            this._sorting = other._sorting;
        }

        if (other._collectionId) {
            this._collectionId = other._collectionId;
        }

        if (other._regionAreas) {
            this._regionAreas = [...this._regionAreas, ...other._regionAreas];
        }

        if (other._fullTags) {
            this._fullTags = other._fullTags;
        }

        if (other._organizer) {
            this._organizer = other._organizer;
        }

        if (other._attributes) {
            this._attributes = {...this._attributes, ...other._attributes};
        }
        if (other._logo !== undefined) {
            this._logo = other._logo;
        }
        if (other._creator !== undefined) {
            this._creator = other._creator;
        }
    }

    set collectionId(collectionId: string | null) {
        this._collectionId = collectionId;
    }
    set x(x: number) {
        this._x = x;
    }
    set y(y: number) {
        this._y = y;
    }
    addRegionArea(area: string) {
        if (this._regionAreas.indexOf(area) === -1) this._regionAreas.push(area);
    }

    get collectionId(): string | null {
        return this._collectionId;
    }

    get creator(): { name: string; slug: string } | null {
        return this._creator;
    }

    get regionAreas() {
        return this._regionAreas;
    }

    get id() {
        return this._id;
    }

    get slug() {
        return this._slug;
    }

    get title() {
        return this._title;
    }

    get address() {
        return this._address;
    }

    get addressString() {
        if (!this._address) return '';

        const street =
            this._address.street != null && this._address.street?.trim() !== ''
                ? this._address.street + ', '
                : '';
        const zip = this._address.zip ? this._address.zip + ' ' : '';
        const locality = this._address.locality || this._address.region || '';
        const localityOrRegion = locality ? locality : '';
        //const country = this._address.country ? this._address.country : '';

        return `${street}${zip}${localityOrRegion}`; //${country}`;
    }

    get locationName() {
        return this._locationName;
    }

    get organizer() {
        return this._organizer;
    }

    get attributes(): EventAttributes {
        return this._attributes;
    }

    get logo(): string | null {
        return this._logo;
    }

    get allDataSourceProviders(): DataSourceProviderInfo[] {
        return this._attributes.allDataSourceProviders || [];
    }

    get canEdit(): boolean {
        return this._attributes?.permissions?.edit === true;
    }

    get latestDate() {
        const now = moment().toDate();
        for (const date of this._dates) {
            if (!date) continue;
            if (date.start >= now) return date;
        }
        // All dates are past — show the most recent one, not the oldest
        return this._dates[this._dates.length - 1] ?? this._dates[0];
    }

    get start() {
        return this.latestDate.start;
    }

    get end() {
        return this.latestDate.end;
    }

    get tags() {
        return this._tags;
    }

    get fullTags() {
        return this._fullTags;
    }

    get futureDates() {
        const startOfToday = moment().startOf('day').toDate();
        return this._dates.filter((d) => d.start >= startOfToday);
    }

    get dates() {
        return this._dates;
    }

    get rawImages() {
        return (this._images || [])
            .map((i) => {
                if (i.url.startsWith('https://beat-assets.s3.amazonaws.com')) {
                    return {
                        ...i,
                        url: i.url.replace(
                            'https://beat-assets.s3.amazonaws.com',
                            'https://cdn.balloon-events.com'
                        ),
                    };
                }
                return i;
            })
            .filter((i) =>
                i.url.startsWith('https://cdn.balloon-events.com') ||
                i.url.startsWith('http://localhost') ||
                /^https?:\/\/(www\.)?(youtube\.com\/watch|youtu\.be\/)/.test(i.url) ||
                isInstagramUrl(i.url)
            );
    }

    get images() {
        if (this.rawImages && this.rawImages.length > 0) {
            return this.rawImages;
        }
        return this.categories.length > 0 && this.categories[0] != null
            ? [this.categories[0].placeholderImage]
            : [];
    }

    get complexDescription() {
        return this._complexDescription;
    }

    get description() {
        return (
            this._complexDescription.getMultiLanguageDescription(
                DescriptionType.CollectiveDescriptionBody
            ) || EMPTY_MULTILANGUAGE_TEXT
        );
    }

    get teaser() {
        return (
            this._complexDescription.getMultiLanguageDescription(
                DescriptionType.Teaser
            ) || EMPTY_MULTILANGUAGE_TEXT
        );
    }

    get locationNotes() {
        return this._complexDescription.getMultiLanguageDescription(
            DescriptionType.Venues
        );
    }

    getDescription(type: DescriptionType) {
        return this._complexDescription.getMultiLanguageDescription(type);
    }

    getTranslatedDescription(type: DescriptionType, language: Language) {
        return this._complexDescription.getDescription(type, language);
    }

    get category() {
        return this._categories[0];
    }

    get categories() {
        return this._categories;
    }

    sortCategories(sort: (a: CategoryModel, b: CategoryModel) => number) {
        this._categories.sort(sort);
    }

    get location() {
        return this._location;
    }

    get fullLocation() {
        return this._fullLocation;
    }

    get source() {
        return this._source;
    }

    get provider() {
        return this._provider;
    }

    set provider(source: Provider | null) {
        this._provider = source;
        this._source = source?.name || '';
    }

    get lastModified() {
        return this._lastModified;
    }

    public set sorting(sorting: number | null) {
        this._sorting = sorting;
    }

    get sorting() {
        return this._sorting;
    }

    get contacts() {
        if (this._contacts.length > 0) return this._contacts;
        if (this._organizer)
            return [
                ...this._organizer.contacts,
                {
                    type: ContactType.Name,
                    content: this._organizer.designation || this._organizer.name,
                },
            ];
        if (
            this._fullLocation.contactInfos != null &&
            this._fullLocation.contactInfos.length > 0
        )
            return [
                ...this._fullLocation.contactInfos,
                {
                    type: ContactType.Name,
                    content: this._fullLocation.name,
                },
            ];
        return [];
    }

    getContact(type: ContactType) {
        return this.contacts.find((c) => c.type === type);
    }

    get balloonDiameterFactor() {
        return Math.max(0.5, Math.min(this.sorting || 0, 1));
        /*
        const start = this.start;
        if (start < tomorrow) return 1;
        if (start < nextWeek) return 0.75;
        return 0.5;*/
    }

    get timeUntilStart() {
        if (this.start.getTime() < Date.now()) {
            if (this.end != null && this.end.getTime() > Date.now()) {
                return 0;
            }
            // No end date: assume 2h duration
            const DEFAULT_DURATION_MS = 2 * 60 * 60 * 1000;
            if (this.end == null && this.start.getTime() + DEFAULT_DURATION_MS > Date.now()) {
                return 0;
            }
            return null;
        }
        return Date.now() - this.start.getTime();
    }
    get x(): number | undefined {
        return this._x;
    }
    get y(): number | undefined {
        return this._y;
    }
}
